import { createRoute, Link } from '@tanstack/react-router';
import { FileX } from 'lucide-react';
import { useCallback } from 'react';

import { EmptyState, ErrorState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePrototypeQuery } from '@/features/prototypes/prototypes-queries';
import { ViewerShell } from '@/features/viewer';

import { rootRoute } from './root.route';

/**
 * The URL contract for a prototype page.
 *
 * `?version=<id>` names which uploaded version is being looked at. It exists
 * because the upload dialog links straight into the version it just created,
 * and a deep link into a specific version is exactly what a reviewer pastes
 * into a message — so it has to be a real, parsable part of the URL rather than
 * component state that a reload would throw away.
 *
 * Parsing is total, like every other search validator here: an id that names
 * nothing simply falls back to the latest version when the viewer lands.
 */
export interface PrototypeDetailSearch {
  /** Version id to open. Absent means the latest. */
  version?: string;
}

export const validatePrototypeDetailSearch = (
  raw: Record<string, unknown>,
): PrototypeDetailSearch => {
  const version = raw['version'];

  return typeof version === 'string' && version.trim() !== ''
    ? { version: version.trim() }
    : {};
};

/** The viewer's own skeleton: a toolbar-shaped bar over a frame-shaped block. */
const ViewerSkeleton = () => (
  <div className="flex h-dvh flex-col overflow-hidden bg-viewer-canvas">
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-viewer-chrome-border bg-viewer-chrome px-3">
      <Skeleton className="size-8 rounded-md" />
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-8 w-12 rounded-md" />
      <div className="ml-auto flex items-center gap-2">
        <Skeleton className="h-8 w-36 rounded-md" />
        <Skeleton className="h-8 w-28 rounded-md" />
        <Skeleton className="h-8 w-24 rounded-md" />
      </div>
    </div>
    <div className="min-h-0 flex-1 p-4 sm:p-6 lg:p-8">
      <Skeleton
        className="h-full w-full rounded-lg"
        aria-label="Loading the prototype"
      />
    </div>
  </div>
);

/**
 * Opening a prototype *is* the viewer (spec R3.1) — there is no intermediate
 * detail screen, so this route is a thin loader around `ViewerShell`.
 *
 * It asks for the prototype and nothing else. That request is what proves the
 * id in the URL means something: it 404s for an unknown prototype, which this
 * page turns into a clean "does not exist" rather than an error. The version
 * list cannot do that job — it answers `200` with an empty array for an id that
 * matches nothing, indistinguishable from a real prototype nobody has uploaded
 * to, and rendering "upload the first version" for a deleted prototype would be
 * a quiet lie. So the shell is only mounted once the prototype is known to
 * exist, and it loads the versions itself.
 *
 * The route also owns the URL half of the version contract: the shell asks for
 * a change, the route performs the navigation. Keeping `navigate` out of the
 * shell is what lets the shell be reasoned about — and tested — without a
 * router.
 */
const PrototypeViewerPage = () => {
  const { prototypeId } = prototypeDetailRoute.useParams();
  const { version } = prototypeDetailRoute.useSearch();
  const navigate = prototypeDetailRoute.useNavigate();
  const prototype = usePrototypeQuery(prototypeId);

  const handleVersionChange = useCallback(
    (versionId: string, replace: boolean) => {
      void navigate({
        to: '/prototypes/$prototypeId',
        params: { prototypeId },
        search: () => ({ version: versionId }),
        replace,
      });
    },
    [navigate, prototypeId],
  );

  const projectId = prototype.data?.projectId ?? null;

  const handleDeleted = useCallback(() => {
    void navigate(
      projectId === null
        ? { to: '/' }
        : { to: '/projects/$projectId', params: { projectId } },
    );
  }, [navigate, projectId]);

  if (prototype.isPending) {
    return <ViewerSkeleton />;
  }

  if (prototype.isError) {
    if (prototype.error.status === 404) {
      return (
        <div className="flex min-h-dvh items-center justify-center p-6">
          <EmptyState
            className="max-w-lg bg-background"
            icon={FileX}
            title="This prototype does not exist"
            description="It may have been deleted, or the link may be wrong. Deleting a prototype takes its versions and comment threads with it."
            action={
              <Button asChild variant="outline">
                <Link to="/">Back to projects</Link>
              </Button>
            }
          />
        </div>
      );
    }

    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <ErrorState
          className="max-w-lg"
          title="Could not load this prototype"
          error={prototype.error}
          onRetry={() => {
            void prototype.refetch();
          }}
        />
      </div>
    );
  }

  return (
    <ViewerShell
      prototype={prototype.data}
      requestedVersionId={version}
      onVersionChange={handleVersionChange}
      onDeleted={handleDeleted}
    />
  );
};

export const prototypeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prototypes/$prototypeId',
  validateSearch: validatePrototypeDetailSearch,
  component: PrototypeViewerPage,
});
